import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm, useFieldArray } from "react-hook-form";
import * as yup from "yup"; // Add yup for validation
import { FormLabel } from "react-bootstrap";

import { EMPTY_STRING } from "../../../constants";
import { AddCountryNotesIcon } from "../../../assets";
import CLOSE_ICON from "../../../assets/icons/close.png";
import RoundedButton from "../../../shared/components/buttons/RoundedButton";
import ConfirmationPopup from "../../../shared/components/modals/ConfirmationPopup";
import MainContentWrapper from "../../../shared/components/main-content-wrapper/MainContentWrapper";
import { adminActions } from "../../../redux/actions";

// Validation Schema using Yup
const schema = yup.object({
  faqs: yup.array().of(
    yup.object({
      question: yup.string().trim().required("Question is required"),
      answer: yup.string().trim().required("Answer is required"),
    })
  ),
});

const initialFAQ = {
  faqId: null,
  question: "",
  answer: "",
  isActive: true,
};

const FAQs = (props) => {
  const { FAQs, updateFAQs, getFAQs } = props;
  // Initialize form with yupResolver for validation
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm({
    resolver: yupResolver(schema),
    mode: "onChange",
    defaultValues: {
      faqs: [initialFAQ],
    },
  });

  // Use useFieldArray hook to handle dynamic fields
  const { fields, append, remove } = useFieldArray({
    control,
    name: "faqs",
  });

  const [targetIndex, setTargetIndex] = useState();
  const [confirmationModal, setConfirmationModal] = useState(false);

  const openConfirmationModal = (index) => {
    setConfirmationModal(true);
    setTargetIndex(index);
  };

  const closeConfirmationModal = () => {
    setConfirmationModal(false);
    setTargetIndex();
  };

  const onSubmit = (data) => {
    const updatedFaqs = data.faqs.map((faq) => ({
      ...faq,
      id: faq.faqId,
    }));
    updateFAQs(updatedFaqs);
  };

  const handleRemoveFAQ = () => {
    const faq = fields[targetIndex];
    if (faq.faqId) {
      const updatedFaqs = [...fields];
      updatedFaqs[targetIndex] = {
        ...updatedFaqs[targetIndex],
        id: faq.id,
        isActive: false,
      };
      setValue("faqs", updatedFaqs);
    } else {
      remove(targetIndex);
    }
    closeConfirmationModal();
  };

  useEffect(() => {
    getFAQs();
  }, []);

  useEffect(() => {
    if (FAQs && FAQs.length > 0) {
      reset({
        faqs: FAQs.map((faq) => ({
          faqId: faq.id,
          question: faq?.question,
          answer: faq.answer,
          isActive: faq.isActive,
        })),
      });
    }
  }, [FAQs, reset]);

  return (
    <MainContentWrapper>
      <ConfirmationPopup
        show={confirmationModal}
        onCancel={closeConfirmationModal}
        onConfirm={handleRemoveFAQ}
        message="Are you sure you want to remove the FAQ?"
      />
      <div className="admin-page-heading">FAQs</div>
      <form className="faq" onSubmit={handleSubmit(onSubmit)}>
        {fields.map(
          (item, index) =>
            item.isActive && (
              <div key={index}>
                <div className="row faq-wrapper">
                  <div className="label">Question:</div>
                  <div className="col-11">
                    <Controller
                      name={`faqs[${index}].question`}
                      control={control}
                      render={({ field }) => (
                        <>
                          <i className="faq-field-highlighter"></i>
                          <input
                            type="text"
                            {...field}
                            className={`faq-field ${
                              errors?.faqs?.[index]?.question
                                ? "border-danger"
                                : EMPTY_STRING
                            }`}
                            placeholder="Enter Question"
                            autoComplete="off"
                          />
                        </>
                      )}
                    />
                  </div>
                  <div
                    className={`${
                      index === 0 ? "d-none" : "col-1 d-flex align-items-center"
                    }`}
                  >
                    <img
                      src={CLOSE_ICON}
                      alt="close icon"
                      className="faq-close-icon"
                      onClick={() => openConfirmationModal(index)} // Remove this field
                    />
                  </div>
                </div>
                <div className="row faq-wrapper">
                  <div className="label">Answer:</div>
                  <div className="col-11">
                    <Controller
                      name={`faqs[${index}].answer`} // Dynamic name for the answer field
                      control={control}
                      render={({ field }) => (
                        <textarea
                          type="text"
                          {...field}
                          className={`faq-text-area ${
                            errors?.faqs?.[index]?.answer
                              ? "border-danger"
                              : EMPTY_STRING
                          }`}
                          placeholder="Enter Answer"
                          autoComplete="off"
                        />
                      )}
                    />
                  </div>
                </div>
              </div>
            )
        )}
        <div className="row faq-wrapper">
          <div
            className="my-2 d-flex gap-2 align-items-center pointer add-country-notes"
            onClick={() => append({ ...initialFAQ })}
          >
            <img src={AddCountryNotesIcon} alt="Add" />
            <div className="add-notes-text">Add More</div>
          </div>
        </div>

        <div className="row faq-wrapper">
          <div className="agent-approval-buttons-container d-flex justify-content-center gap-3 mt-5">
            <RoundedButton type="submit">Submit</RoundedButton>
          </div>
        </div>
      </form>
    </MainContentWrapper>
  );
};

const mapStateToProps = (state) => ({
  FAQs: state.adminReducer.FAQs,
});

const mapDispatchToProps = (dispatch) => ({
  updateFAQs: (data) => dispatch(adminActions.updateFAQs(data)),
  getFAQs: () => dispatch(adminActions.getFAQs()),
});

export default connect(mapStateToProps, mapDispatchToProps)(FAQs);
